/**
 * Enable Control Button Component
 * Handles SCADA write session authentication with basic flow
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Chip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { scadaWriteAPI } from '../../services/scadaWriteService';
import { WriteSessionStatus, WriteSessionRequest, WriteSessionResponse } from '../../types/scada';

interface EnableControlButtonProps {
  siteId: number;
  siteName?: string;
  onSessionStart?: (sessionToken: string) => void;
  onSessionEnd?: () => void;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined' | 'text';
  userPermissions?: {
    can_write_scada_tags: boolean;
    role: string;
  };
  sessionDurationMinutes?: number;
}

export const EnableControlButton: React.FC<EnableControlButtonProps> = ({
  siteId,
  siteName,
  onSessionStart,
  onSessionEnd,
  size = 'medium',
  variant = 'contained',
  userPermissions,
  sessionDurationMinutes = 10,
}) => {
  const [sessionStatus, setSessionStatus] = useState<WriteSessionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [backupToken, setBackupToken] = useState('');
  const [remainingTime, setRemainingTime] = useState<number | null>(null);

  // Check session status on mount and periodically
  useEffect(() => {
    checkSessionStatus();
    const interval = setInterval(checkSessionStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [siteId]);

  // Update remaining time
  useEffect(() => {
    if (sessionStatus?.session_active && sessionStatus.remaining_seconds) {
      setRemainingTime(sessionStatus.remaining_seconds);
      const interval = setInterval(() => {
        setRemainingTime(prev => {
          if (prev && prev > 0) {
            return prev - 1;
          } else {
            checkSessionStatus(); // Refresh status when time expires
            return null;
          }
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [sessionStatus]);

  const checkSessionStatus = async () => {
    try {
      const status = await scadaWriteAPI.getWriteSessionStatus(siteId);
      setSessionStatus(status);
      setError(null);
    } catch (err: any) {
      console.error('Failed to check session status:', err);
      setError('Failed to check session status');
    }
  };

  const handleStartSession = async () => {
    if (!sessionStatus?.requires_2fa) {
      // No 2FA required, start session directly
      try {
        setIsLoading(true);
        const request: WriteSessionRequest = { site_id: siteId };
        const response = await scadaWriteAPI.startWriteSession(request);
        
        if (response.success && response.session_token) {
          onSessionStart?.(response.session_token);
          await checkSessionStatus();
          setError(null);
        } else {
          setError(response.error || 'Failed to start session');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to start write session');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Show TOTP dialog
      setShowStartDialog(true);
    }
  };

  const handleTotpSubmit = async () => {
    try {
      setIsLoading(true);
      const request: WriteSessionRequest = {
        site_id: siteId,
        totp_code: totpCode || undefined,
        backup_token: backupToken || undefined,
      };
      
      const response = await scadaWriteAPI.startWriteSession(request);
      
      if (response.success && response.session_token) {
        onSessionStart?.(response.session_token);
        await checkSessionStatus();
        setShowStartDialog(false);
        setTotpCode('');
        setBackupToken('');
        setError(null);
      } else {
        setError(response.error || 'Failed to start session');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start write session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndSession = async () => {
    try {
      setIsLoading(true);
      await scadaWriteAPI.endWriteSession(siteId);
      onSessionEnd?.();
      await checkSessionStatus();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to end write session');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isSessionActive = sessionStatus?.session_active === true;

  // Check if user has permission to control SCADA
  const hasWritePermission = userPermissions?.can_write_scada_tags ?? false;
  const userRole = userPermissions?.role || 'unknown';

  // Don't show control button if user doesn't have permission
  if (!hasWritePermission) {
    return null;
  }

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        {isSessionActive ? (
          <>
            <Button
              variant="contained"
              color="error"
              size={size}
              onClick={handleEndSession}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : <LockIcon />}
              sx={{
                background: 'linear-gradient(45deg, #f44336 30%, #ff6b6b 90%)',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                  '70%': { boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' }
                }
              }}
            >
              {isLoading ? 'Ending...' : '🔴 Control Active'}
            </Button>
            {remainingTime !== null && remainingTime > 0 && (
              <Chip
                icon={<TimerIcon />}
                label={`${formatTime(remainingTime)} remaining`}
                color="success"
                size="small"
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontWeight: 'bold',
                  animation: remainingTime <= 60 ? 'blink 1s infinite' : 'none',
                  '@keyframes blink': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }}
              />
            )}
          </>
        ) : (
          <Button
            variant={variant}
            color="primary"
            size={size}
            onClick={handleStartSession}
            disabled={isLoading || !sessionStatus?.can_write}
            startIcon={isLoading ? <CircularProgress size={16} /> : <UnlockIcon />}
            sx={{
              '&:hover': {
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              }
            }}
          >
            {isLoading ? 'Starting...' : '🔓 Enable Control'}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}


      {/* TOTP Input Dialog */}
      <Dialog open={showStartDialog} onClose={() => setShowStartDialog(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon />
            Enable SCADA Control - {siteName}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter your authenticator code or backup token to enable SCADA write operations for {sessionDurationMinutes} minutes.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Role:</strong> {userRole}<br/>
              <strong>Duration:</strong> {sessionDurationMinutes} minutes<br/>
              <strong>Access Level:</strong> SCADA Write Control
            </Typography>
          </Alert>
          
          <TextField
            fullWidth
            label="Authenticator Code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            placeholder="6-digit code"
            margin="normal"
            inputProps={{ maxLength: 6 }}
          />
          
          <Typography variant="body2" sx={{ my: 1, textAlign: 'center' }}>
            OR
          </Typography>
          
          <TextField
            fullWidth
            label="Backup Token"
            value={backupToken}
            onChange={(e) => setBackupToken(e.target.value)}
            placeholder="Backup recovery token"
            margin="normal"
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStartDialog(false)}>Cancel</Button>
          <Button
            onClick={handleTotpSubmit}
            variant="contained"
            disabled={isLoading || (!totpCode && !backupToken)}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Enable Control'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
